/**
 * The "Send Feedback" drawer.
 *
 * Opens over whichever page you were on, already knowing which page that is — the surface is
 * resolved from the route and shown in the banner rather than asked for, because a reporter
 * naming their own tab is exactly the field that gets left blank or filled in wrong.
 *
 * Screenshots are sent base64 and uploaded to Slack by the proxy, into the report's thread (see
 * feedbackApi.ts for why not Firebase Storage).
 *
 * On the Candidate Requests tab it also offers a picker for the request being reported on. That
 * attaches the candidate's message and our drafted reply — the only path on which candidate data
 * leaves the app, and one that depends on #people_spaces_feedback staying PRIVATE.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Drawer, DrawerContent, DrawerFooter } from '@finn-auto/design-system/atoms/drawer';
import { Input } from '@finn-auto/design-system/atoms/input';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Autocomplete } from '@finn-auto/design-system/atoms/autocomplete';
import { Camera, Info, X } from 'lucide-react';
import { cn } from '../../../shared';
import { useAuth } from '../../../contexts/AuthContext';
import { filterRequestOptions, listRequestOptions, toRequestContext } from './requestOptions';
import { SegmentedField } from './SegmentedField';
import { DescriptionFields } from './DescriptionFields';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_TYPES,
  MAX_SCREENSHOTS,
  MAX_SCREENSHOT_BYTES,
  submitFeedback,
  toFeedbackScreenshot,
  type FeedbackPriority,
  type FeedbackType,
} from '../services/feedbackApi';
import { describeSurface, type FeedbackSurface } from './feedbackSurfaces';

/** Shown in the banner so a reporter can see where this lands before they write it. */
export const FEEDBACK_CHANNEL_LABEL = '#people_spaces_feedback';

const TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'Bug',
  feature: 'Feature',
  improvement: 'Improvement',
  other: 'Other',
};

const PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

/** Sets expectations before someone picks the top priority: critical means same-day. */
const PRIORITY_HINTS: Partial<Record<FeedbackPriority, string>> = {
  critical: 'Usually fixed today',
};

export default function FeedbackPanel({
  surface,
  open,
  onOpenChange,
}: {
  surface: FeedbackSurface;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [type, setType] = useState<FeedbackType | null>(null);
  const [priority, setPriority] = useState<FeedbackPriority | null>(null);
  const [title, setTitle] = useState('');
  const [problem, setProblem] = useState('');
  const [expected, setExpected] = useState('');
  const [example, setExample] = useState('');
  /**
   * Each screenshot is held with the preview URL made for it, rather than deriving URLs from the
   * files on every render: a URL created in render has no matching revoke, so the previews leak
   * one blob per keystroke. Created on add, revoked on remove and on unmount.
   */
  const [shots, setShots] = useState<{ file: File; url: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pageLabel = describeSurface(surface);

  /**
   * The Candidate Requests picker, shown ONLY on that tab. Read once when the panel opens: the
   * queue can refresh underneath (the tab reloads itself after 15 idle minutes), and a dropdown
   * whose options shuffle while you are choosing is worse than a slightly stale list.
   */
  const { user } = useAuth();
  const isRequestsTab = surface.id === 'ta-action-boards' && surface.subTab === 'Requests';
  const [requestOptions] = useState(() =>
    isRequestsTab ? listRequestOptions(user?.email ?? '') : []
  );
  const [threadId, setThreadId] = useState<string>('');
  const [requestQuery, setRequestQuery] = useState('');
  const chosen = requestOptions.find((o) => o.threadId === threadId) ?? null;
  // Capped: the dropdown is a picker, not a results page, and 40 rows in a drawer is a scroll
  // rather than a choice. Typing narrows it, and the count below says when there is more.
  const matches = filterRequestOptions(requestOptions, requestQuery);
  const shownMatches = matches.slice(0, 8);

  // Closing the drawer unmounts the panel (see FeedbackWidget), so this is what frees the blobs.
  // The ref is tracked in its own effect so the unmount cleanup can read the LAST set of shots
  // without re-running — a cleanup keyed on `shots` would revoke a preview still on screen.
  const shotsRef = useRef(shots);
  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);
  useEffect(() => () => shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url)), []);

  const reset = useCallback(() => {
    setType(null);
    setPriority(null);
    setTitle('');
    setProblem('');
    setExpected('');
    setExample('');
    setShots((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.url));
      return [];
    });
  }, []);

  const addFiles = useCallback((incoming: FileList | File[] | null) => {
    const picked = Array.from(incoming ?? []);
    if (!picked.length) return;
    setShots((prev) => {
      const room = MAX_SCREENSHOTS - prev.length;
      if (room <= 0) {
        showToast({ status: 'error', description: `You can attach up to ${MAX_SCREENSHOTS} screenshots.` });
        return prev;
      }
      const accepted: { file: File; url: string }[] = [];
      for (const file of picked.slice(0, room)) {
        if (!file.type.startsWith('image/')) {
          showToast({ status: 'error', description: `${file.name} is not an image — screenshots only.` });
          continue;
        }
        if (file.size > MAX_SCREENSHOT_BYTES) {
          showToast({
            status: 'error',
            description: `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 3 MB.`,
          });
          continue;
        }
        accepted.push({ file, url: URL.createObjectURL(file) });
      }
      if (picked.length > room) {
        showToast({ status: 'error', description: `Only ${MAX_SCREENSHOTS} screenshots fit — the rest were skipped.` });
      }
      return accepted.length ? [...prev, ...accepted] : prev;
    });
  }, []);

  // Cmd+V straight into the panel is how a screenshot actually gets attached; making people save
  // to disk first is the step where a bug report stops being worth filing.
  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const images = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
      if (images.length) {
        e.preventDefault();
        addFiles(images);
      }
    },
    [addFiles]
  );

  /**
   * A greyed-out Submit is indistinguishable from a broken one — the click just vanishes. So the
   * panel names what is still missing instead of leaving the reporter to guess which of the six
   * required fields it is.
   */
  const missing = [
    !type && 'Type',
    !priority && 'Priority',
    !title.trim() && 'Title',
    !problem.trim() && 'Problem',
    !expected.trim() && 'What should happen',
    !example.trim() && 'Example',
  ].filter(Boolean) as string[];

  const canSubmit = missing.length === 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !type || !priority) return;
    setSubmitting(true);
    try {
      const screenshots = await Promise.all(shots.map((s) => toFeedbackScreenshot(s.file)));

      const result = await submitFeedback({
        type,
        priority,
        title: title.trim(),
        problem: problem.trim(),
        expected: expected.trim(),
        example: example.trim(),
        description: [problem, expected, example].map((part) => part.trim()).join('\n\n'),
        surface: pageLabel,
        surfaceId: surface.id,
        path: window.location.pathname,
        screenshots,
        request: chosen ? toRequestContext(chosen.request) : null,
      });

      // Categories and counts only — never the title or description. The PII rule in
      // src/analytics/segment.ts applies here too: a bug report can quote a candidate.
      void logAnalyticsEvent('feedback_submitted', {
        feedback_type: type,
        feedback_priority: priority,
        surface: surface.id,
        sub_tab: surface.subTab ?? 'none',
        screenshots: screenshots.length,
        // A BOOLEAN. Never the thread id, the candidate or the draft — the PII rule applies to
        // analytics regardless of what the private Slack channel is allowed to carry.
        has_request_context: Boolean(chosen),
      });

      // The report landed either way; say so honestly if a screenshot did not make it, rather
      // than letting the reporter believe an image they attached is in the channel.
      const lost = screenshots.length - (result.screenshots?.shared ?? screenshots.length);
      showToast({
        status: lost > 0 ? 'warning' : 'success',
        description:
          lost > 0
            ? `Sent to ${FEEDBACK_CHANNEL_LABEL}, but ${lost} screenshot${lost > 1 ? 's' : ''} could not be attached.`
            : `Thanks — sent to ${FEEDBACK_CHANNEL_LABEL}.`,
      });
      reset();
      onOpenChange(false);
    } catch (e) {
      showToast({
        status: 'error',
        description:
          e instanceof Error && e.message ? e.message : 'Could not send your feedback. Nothing was sent.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      direction="right"
      onOpenChange={(next) => {
        if (submitting) return; // a half-sent report must not vanish under the user
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DrawerContent
        childrenClassName="!gap-5 !p-6"
        renderHeader={() => (
          <div className="flex items-start justify-between gap-4 border-0 border-b border-solid border-zinc-200 p-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Send Feedback</h2>
              <p className="mt-1 text-sm text-zinc-500">
                Share bugs, ideas, or improvements — the page stays visible behind this panel.
              </p>
            </div>
            {/* Disabled mid-send for the same reason the drawer's own dismiss is: this button
                bypasses onOpenChange's guard, so without it a click here could unmount a report
                that is already on its way to Slack. */}
            <button
              type="button"
              disabled={submitting}
              onClick={() => !submitting && onOpenChange(false)}
              aria-label="Close feedback panel"
              className="shrink-0 rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      >
        <div onPaste={onPaste} className="flex flex-col gap-5">
          {/* The pre-filled context. Shown, not asked for. */}
          <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="text-sm">
              <div className="font-semibold text-zinc-900">Goes to {FEEDBACK_CHANNEL_LABEL}</div>
              <div className="text-zinc-600">
                About: <span className="font-medium text-zinc-800">{pageLabel}</span>
              </div>
            </div>
          </div>

          {isRequestsTab && (
            <div>
              <div className="mb-1.5 text-sm font-semibold text-zinc-800">
                Which request is this about? <span className="font-normal text-zinc-400">(optional)</span>
              </div>

              {requestOptions.length === 0 ? (
                <p className="text-sm text-zinc-500">
                  No requests loaded in this tab yet — open Candidate requests and let the queue
                  load, then report from there to attach one.
                </p>
              ) : (
                <>
                  <Autocomplete
                    aria-label="Which request is this about?"
                    placeholder="Type a candidate name, address or subject…"
                    openOnFocus
                    disabled={submitting}
                    options={shownMatches.map((o) => ({ value: o.threadId, label: o.label }))}
                    value={chosen ? { value: chosen.threadId, label: chosen.label } : undefined}
                    onInputValueChange={setRequestQuery}
                    onValueChange={(option) => setThreadId(option?.value ?? '')}
                  />

                  {requestQuery.trim() && matches.length === 0 && (
                    <p className="mt-1.5 text-sm text-zinc-500">
                      Nothing in the loaded queue matches “{requestQuery.trim()}”.
                    </p>
                  )}
                  {matches.length > shownMatches.length && (
                    <p className="mt-1.5 text-sm text-zinc-400">
                      Showing {shownMatches.length} of {matches.length} — keep typing to narrow it.
                    </p>
                  )}

                  {chosen && (
                    <div className="mt-2 flex items-start justify-between gap-3 rounded-lg bg-amber-50 p-3 text-sm">
                      <span className="text-zinc-700">
                        The candidate's message and our drafted reply will be posted in the thread,
                        visible to everyone in {FEEDBACK_CHANNEL_LABEL}.
                      </span>
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => {
                          setThreadId('');
                          setRequestQuery('');
                        }}
                        className="shrink-0 font-medium text-zinc-500 underline hover:text-zinc-800 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <SegmentedField
            label="Type"
            options={FEEDBACK_TYPES}
            labels={TYPE_LABELS}
            value={type}
            onChange={setType}
            disabled={submitting}
          />

          <SegmentedField
            label="Priority"
            options={FEEDBACK_PRIORITIES}
            labels={PRIORITY_LABELS}
            hints={PRIORITY_HINTS}
            value={priority}
            onChange={setPriority}
            disabled={submitting}
          />

          <Input
            label="Title *"
            value={title}
            disabled={submitting}
            maxLength={140}
            onChange={(e) => setTitle(e.target.value)}
          />

          <DescriptionFields
            type={type}
            disabled={submitting}
            problem={problem}
            expected={expected}
            example={example}
            onProblem={setProblem}
            onExpected={setExpected}
            onExample={setExample}
          />

          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-sm font-semibold text-zinc-800">Screenshots (optional)</span>
              <span className="text-sm text-zinc-400">
                {shots.length}/{MAX_SCREENSHOTS}
              </span>
            </div>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                addFiles(e.dataTransfer.files);
              }}
              className={cn(
                'flex flex-wrap gap-3 rounded-lg p-1 transition-colors',
                dragging && 'bg-blue-50 ring-2 ring-blue-300'
              )}
            >
              {shots.map((shot) => (
                <div key={shot.url} className="relative h-24 w-24 overflow-hidden rounded-lg border border-zinc-200">
                  <img src={shot.url} alt={shot.file.name} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    disabled={submitting}
                    aria-label={`Remove ${shot.file.name}`}
                    onClick={() =>
                      setShots((prev) => {
                        URL.revokeObjectURL(shot.url);
                        return prev.filter((s) => s.url !== shot.url);
                      })
                    }
                    className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white transition-colors hover:bg-black/80"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}

              {shots.length < MAX_SCREENSHOTS && (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => inputRef.current?.click()}
                  className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-300 text-zinc-400 transition-colors hover:border-blue-400 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Camera className="h-5 w-5" />
                  <span className="text-sm">Add</span>
                </button>
              )}
            </div>

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <p className="mt-2 text-sm text-zinc-400">
              Drag, drop or paste up to {MAX_SCREENSHOTS} images (3 MB each). They are uploaded
              into the Slack thread, visible to everyone in {FEEDBACK_CHANNEL_LABEL}.
            </p>
          </div>

          {missing.length > 0 && (
            <p className="text-sm text-zinc-500" role="status">
              Still needed before you can send: <span className="font-medium text-zinc-700">{missing.join(', ')}</span>
            </p>
          )}
        </div>

        <DrawerFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} loading={submitting} onClick={handleSubmit}>
            Submit Feedback
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
